/*
 * De regels achter het verlengingsscherm, los van het scherm zelf.
 *
 * Ze stonden tussen de 1000 regels van EmployeeEditExtension.vue en
 * NewEmployee.vue, waar ze niet te testen waren: dit project heeft geen
 * testopzet die Vue-componenten kan monteren, en die componenten hangen via
 * $parent en $refs zo aan elkaar dat monteren veel stubwerk zou kosten.
 *
 * Deze functies zijn pure invoer naar uitvoer en worden wel getest, met de
 * tsc die hier al voor de frontend staat en zonder extra pakketten. Zie
 * tests/logic/verlenging.test.js.
 */
import moment from 'moment';

/**
 * De vroegste startdatum die een verlenging mag hebben.
 *
 * Begint de verlenging op of voor de startdatum van het contract dat verlengd
 * wordt, dan krijgt NMBRS twee perioden die over elkaar heen liggen, en dat
 * kunnen wij niet terugdraaien. Zie IHR-129.
 *
 * @param {string|null} vorigeStartdatum startdatum van het lopende contract
 * @returns {string|null} datum als YYYY-MM-DD, of null als er geen grens is
 */
export function vroegsteStartdatum(vorigeStartdatum){
  if(!vorigeStartdatum) return null;
  const datum = moment(vorigeStartdatum);
  if(!datum.isValid()) return null;
  return datum.add(1, 'days').format('YYYY-MM-DD');
}

/**
 * Sluit de startdatum aan op het vorige contract?
 *
 * Niet aansluiten is geen fout maar precies wat een correctie doet, dus dit is
 * de basis voor een waarschuwing en niet voor een blokkade. Zie IHR-129.
 *
 * Ontbreekt een van de twee datums, dan valt er niets te waarschuwen en
 * antwoorden we true: geen onbekende waarde als afwijking tonen.
 */
export function startdatumSluitAan(startdatum, vorigeEinddatum){
  if(!startdatum || !vorigeEinddatum) return true;
  const start = moment(startdatum);
  const eind = moment(vorigeEinddatum);
  if(!start.isValid() || !eind.isValid()) return true;
  return start.format('YYYY-MM-DD') === eind.add(1, 'days').format('YYYY-MM-DD');
}

/**
 * Het dienstverband zoals het scherm het kan tonen.
 *
 * NMBRS kent een parttimesalaris en het portaal heeft daar geen knop voor: de
 * keuzegroep Dienstverband biedt alleen Vast (fulltime), Uurloon en Stage. Kwam
 * een contract als parttime terug, dan stond er niets aangevinkt en koos de
 * gebruiker opnieuw, waarna het contract een wijziging meldde die er niet was.
 *
 * Vast is hier de juiste knop en niet een benadering: storeSalary() in de
 * backend leidt het NMBRS-salaristype zelf weer af uit de uren (38 uur is
 * fulltime, al het andere parttime), dus de medewerker gaat ongewijzigd terug.
 * Zie IHR-186.
 */
export function dienstverbandVoorScherm(dienstverband){
  return dienstverband === 'parttime' ? 'fulltime' : dienstverband;
}

/**
 * Een fulltime contract van nul uur met een salaris onder de 1000 is in de
 * praktijk een uurloner.
 *
 * Deze afleiding stond op drie plekken in NewEmployee.vue, telkens met dezelfde
 * drie getallen. Hier staat hij een keer.
 */
export function dienstverbandUitUrenEnSalaris(jobFunction){
  if(!jobFunction) return jobFunction;
  if(jobFunction.employment_type === 'fulltime'
      && Number(jobFunction.hours) === 0
      && Number(jobFunction.salary) < 1000){
    return 'hourly';
  }
  return jobFunction.employment_type;
}

/**
 * De uren per week zoals ze in de tekst van een verlenging horen te staan:
 * afgerond op twee decimalen, met een komma, zonder overbodige nullen.
 *
 * Het rooster wordt in de browser opgeteld, en vijf dagen van 6,40 uur is voor
 * JavaScript 31.999999999999996. Dat getal ging onbewerkt de zin "Je zult in
 * dienst zijn voor ... uur per week" in, in een document dat de medewerker
 * ondertekent. De notatie is dezelfde als die van getHoursCleaned() in de
 * schermen, zodat de verlenging en het contract er hetzelfde uitzien.
 *
 * Ontbrekende of onleesbare uren worden "0": dat is ook wat het rooster zelf
 * optelt als er niets is ingevuld.
 */
export function urenAlsTekst(uren){
  const getal = Number(uren);
  if(uren === null || uren === '' || !isFinite(getal)) return '0';
  // toFixed rondt af, de Number eromheen haalt de nullen achteraan weg.
  return String(Number(getal.toFixed(2))).replace('.', ',');
}
